import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { StarUpPopup, diffVaultTab } from "../components/vault/StarUpPopup";
import { ClassIcon } from "../components/ClassIcon";
import api from "../lib/api";
import { Star, Skull, Lock, Package, Award, Shirt, Layers, Crown, Trophy, Plane, UtensilsCrossed } from "lucide-react";
import { GradCard, SectionHeading } from "../components/wrestler/WrestlerUI";
import { HeroStat } from "../components/promotion/PromotionUI";
import { SLOT_META, SlotChip, ShopFrame } from "../components/shop/ShopUI";
import { StarRow, MiniChip, StatChip, VaultTab, FilterPill, TierScale, STAT_META } from "../components/vault/VaultUI";

function CardItem({ card, draftSelected, draftFull, onToggleDraft, delay }) {
  const owned = card.count > 0;
  const isBoss = card.is_boss_card;
  const canDraft = owned && (draftSelected || !draftFull);
  const maxT = card.max_tier || 4;
  const statColor = (STAT_META[card.bonus_stat] || STAT_META.all).color;
  const glow = draftSelected ? "#1cff73" : owned && isBoss ? "#9D00FF" : owned ? statColor : "#2D1C52";
  return (
    <div className="wr-rise" style={{ animationDelay: `${delay}ms` }}>
      <div
        data-testid={`card-${card.id}`}
        title={`Stage ${card.stage} · ${card.name}\nOwned: ${card.count}\nTier ${card.tier}/${maxT} · +${card.bonus_pct}% ${card.bonus_stat}${draftSelected ? "  (DRAFTED ×2)" : ""}${owned ? "\nClick to " + (draftSelected ? "deselect" : "draft (×2 bonus)") : ""}`}
        onClick={() => { if (owned && (draftSelected || canDraft)) onToggleDraft(card.id); }}
        className={`vault-card group relative rounded-xl p-[2px] transition-transform duration-200 hover:-translate-y-1 ${owned ? "cursor-pointer" : "opacity-50"}`}
        style={{ background: owned ? `linear-gradient(150deg, ${glow}, #5B3BFF55, ${glow}88)` : "linear-gradient(150deg, #2D1C52, #1C0D40)", "--vault-glow": glow,
                 boxShadow: draftSelected ? "0 0 16px rgba(255,159,28,0.7)" : owned && isBoss ? "0 0 12px rgba(157,0,255,0.45)" : undefined }}>
        <div className="relative rounded-[10px] p-1.5 h-full" style={{ background: owned ? `linear-gradient(180deg, ${statColor}22, #0A051A 60%)` : "#0E0620" }}>
          <div className="absolute top-1 left-1 px-1 rounded text-[8px] font-mono font-bold bg-[#0A051A]/85 z-10" style={{ color: owned ? statColor : "#695D8E" }} data-testid={`card-stage-${card.id}`}>S{card.stage}</div>
          {isBoss && <div className="absolute top-1 right-1 px-1 rounded text-[8px] font-heading font-bold z-10" style={{ background: "#9D00FF", color: "white" }} data-testid={`card-boss-${card.id}`}>B</div>}
          {draftSelected && (
            <div className="absolute -top-2 -right-2 w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-heading font-bold z-20"
                 style={{ background: "#FFE600", color: "#0A051A", boxShadow: "0 0 8px #FFE600AA" }} data-testid={`card-drafted-${card.id}`}>×2</div>
          )}
          <div className="aspect-[3/4] rounded-lg flex items-center justify-center my-1 overflow-hidden relative bg-[#0A051A]">
            {owned && card.image ? (
              <img src={card.image} alt={card.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                   onError={(e) => { e.currentTarget.style.display = "none"; }} />
            ) : owned ? (
              isBoss ? <Skull size={34} style={{ color: card.color, filter: `drop-shadow(0 0 6px ${card.color})` }} />
                     : <div className="text-3xl font-black" style={{ color: card.color }}>X</div>
            ) : <Lock size={20} className="text-[#3D2A5A]" />}
            {owned && <span className="wr-tile-shine pointer-events-none absolute inset-y-0 -left-1/2 w-1/3 opacity-0 group-hover:opacity-100 group-hover:animate-[wrTileShine_0.8s_ease-out]" />}
          </div>
          <div className="flex justify-center mb-1"><StarRow tier={card.tier} max={maxT} size={maxT > 4 ? 8 : 10} /></div>
          <div className="font-heading text-[10px] text-center leading-tight truncate uppercase tracking-tight mb-1" style={{ color: owned ? "#FFFFFF" : "#695D8E" }}>{card.name}</div>
          <div className="grid grid-cols-1 gap-[3px]">
            <div className="grid grid-cols-1 gap-[3px]">
              <MiniChip icon={Layers} color="#00F0FF" dim={!owned} testId={`card-count-${card.id}`}>x{card.count.toLocaleString()}</MiniChip>
              <MiniChip icon={Skull} color="#FF3399" dim={!(card.kills > 0)} testId={`card-kills-${card.id}`}>{(card.kills || 0).toLocaleString()}</MiniChip>
            </div>
            <StatChip stat={card.bonus_stat} value={card.bonus_pct} suffix="%" dim={!owned} testId={`card-bonus-${card.id}`} />
          </div>
          {owned && card.next_tier_at !== null && (
            <div className="mt-1.5 bg-[#0A051A] rounded-full overflow-hidden border border-[#2D1C52]" style={{ height: 4 }}>
              <div className="h-full" style={{ width: `${Math.min(100, (card.count / card.next_tier_at) * 100)}%`, background: `linear-gradient(90deg, ${statColor}, #FFE600)` }} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TrophyItem({ t, delay }) {
  const owned = t.owned;
  const stat = STAT_META[t.bonus_stat] || STAT_META.all;
  const color = owned ? stat.color : "#4A3470";
  return (
    <ShopFrame testId={`trophy-${t.id}`} color={color} alt={owned ? "#5B3BFF" : "#2D1C52"} dim={!owned} delay={delay}>
      <div className="relative p-2.5 flex flex-col h-full">
        <div className="relative aspect-square rounded-xl bg-[#0A051A] mb-2 overflow-hidden border-2" style={{ borderColor: `${color}77` }}>
          {t.image ? (
            <img src={t.image} alt={t.name} className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-110 ${owned ? "" : "grayscale brightness-50"}`}
                 onError={(e) => { e.currentTarget.style.display = "none"; }} />
          ) : <div className="w-full h-full flex items-center justify-center text-4xl">{t.icon}</div>}
          <span className="wr-tile-shine pointer-events-none absolute inset-y-0 -left-1/2 w-1/3 opacity-0 group-hover:opacity-100 group-hover:animate-[wrTileShine_0.8s_ease-out]" />
          <div className="absolute top-1.5 left-1.5 w-7 h-7 rounded-lg flex items-center justify-center bg-black/70 backdrop-blur border" style={{ borderColor: color }}>
            {owned ? <Trophy size={14} style={{ color, filter: `drop-shadow(0 0 4px ${color})` }} /> : <Lock size={13} className="text-[#695D8E]" />}
          </div>
          {owned && <div className="absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded-full text-[9px] font-heading uppercase tracking-widest text-[#0A051A]" style={{ background: "#39FF14", boxShadow: "0 0 8px #39FF14" }}>Active</div>}
        </div>
        <div className="font-graffiti text-base leading-tight truncate" style={{ color: owned ? stat.color : "#A89BCC", textShadow: owned ? `0 0 8px ${stat.color}66, 2px 2px 0 #000` : "2px 2px 0 #000" }} title={t.name}>{t.name}</div>
        <div className="text-[10px] font-mono text-[#C9BDF0] leading-snug mt-0.5 mb-2 line-clamp-2" title={t.desc}>{t.desc}</div>
        <div className="mt-auto grid grid-cols-1 gap-[3px]">
          <MiniChip icon={t.boss_stage ? Crown : t.source === "bundle" ? Award : Plane} color="#9D00FF" testId={`trophy-source-${t.id}`}>{t.boss_stage ? `Boss Stage ${t.boss_stage}` : t.source === "bundle" ? "Founder Bundle" : "World Tour"}</MiniChip>
          <MiniChip icon={stat.icon} color={owned ? "#39FF14" : stat.color} dim={!owned} testId={`trophy-bonus-${t.id}`}>
            +{t.bonus_pct}% {t.bonus_stat === "all" ? "ALL STATS" : stat.short} {owned ? "" : "· LOCKED"}
          </MiniChip>
          {t.xp_bonus_pct > 0 && (
            <MiniChip icon={Award} color={owned ? "#39FF14" : "#4A3470"} dim={!owned} testId={`trophy-xp-bonus-${t.id}`}>
              +{t.xp_bonus_pct}% XP {owned ? "" : "· LOCKED"}
            </MiniChip>
          )}
        </div>
      </div>
    </ShopFrame>
  );
}

function CollectibleItem({ c, delay }) {
  const owned = c.qty > 0;
  const col = owned ? (c.is_boss_drop ? "#9D00FF" : "#39FF14") : "#2D1C52";
  return (
    <div className="wr-rise" style={{ animationDelay: `${delay}ms` }}>
      <div data-testid={`collectible-${c.id}`} title={`${c.name} — Stage ${c.stage} · Owned: ${c.qty}`}
           className={`vault-card group relative rounded-xl p-[2px] transition-transform duration-200 hover:-translate-y-1 ${owned ? "" : "opacity-50"}`}
           style={{ background: `linear-gradient(150deg, ${col}, #5B3BFF44, ${col}88)`, "--vault-glow": col }}>
        <div className="relative rounded-[10px] p-1.5" style={{ background: owned ? `linear-gradient(180deg, ${col}22, #0A051A 60%)` : "#0E0620" }}>
          <div className="absolute top-1 left-1 px-1 rounded text-[8px] font-mono font-bold bg-[#0A051A]/85 z-10" style={{ color: owned ? col : "#695D8E" }} data-testid={`collectible-stage-${c.id}`}>S{c.stage}</div>
          {c.is_boss_drop && <div className="absolute top-1 right-1 px-1 rounded text-[8px] font-heading font-bold z-10" style={{ background: "#9D00FF", color: "white" }} data-testid={`collectible-boss-${c.id}`}>B</div>}
          <div className="aspect-[3/4] rounded-lg overflow-hidden my-1 bg-[#0A051A] flex items-center justify-center">
            {owned && c.image ? (
              <img src={c.image} alt={c.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" onError={(e) => { e.currentTarget.style.display = "none"; }} />
            ) : <Package size={22} className={owned ? "text-[#39FF14]" : "text-[#3D2A5A]"} />}
          </div>
          <div className="font-heading text-[10px] text-center leading-tight truncate uppercase tracking-tight mb-1" style={{ color: owned ? "#FFFFFF" : "#695D8E" }}>{c.name}</div>
          <MiniChip icon={Layers} color="#39FF14" dim={!owned}>x{c.qty.toLocaleString()}</MiniChip>
        </div>
      </div>
    </div>
  );
}

function WrestlerPicker({ g, wrestlers, exclude, onPick }) {
  const list = wrestlers.filter((w) => !exclude.includes(w.wrestler_id));
  return (
    <div className="mt-2 p-2 rounded-lg bg-[#0A051A] border border-[#00F0FF]/40" data-testid={`gear-picker-${g.item_id}`}>
      <div className="text-[10px] font-mono text-[#A89BCC] mb-1 uppercase tracking-widest">Choose wrestler</div>
      {list.length === 0 ? <div className="text-[10px] font-mono text-[#695D8E]">Every wrestler already has this.</div> : (
        <div className="grid grid-cols-1 gap-1">
          {list.map((w) => (
            <button key={w.wrestler_id} data-testid={`gear-pick-${g.item_id}-${w.wrestler_id}`} onClick={() => onPick(w.wrestler_id)}
                    className="flex items-center gap-2 px-2 py-1 rounded hover:bg-[#00F0FF]/10 transition-colors text-left">
              <ClassIcon classId={w.class_info?.id} color={w.class_info?.color || "#00F0FF"} size={13} testId={`gear-pick-class-icon-${w.wrestler_id}`} />
              <span className="text-xs font-mono flex-1 truncate">{w.name}</span>
              <span className="text-[9px] font-mono text-[#695D8E]">LV {w.level}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function GearItem({ g, wrestlers, onEquip, onUnequip, delay }) {
  const [open, setOpen] = useState(false);
  const isFood = g.slot === "food";
  const wearer = g.equipped_by;
  const foodWearers = g.food_wearers || [];
  const m = SLOT_META[g.slot] || SLOT_META.all;
  const SlotIcon = m.icon;
  const maxed = g.next_at === null || g.next_at === undefined;
  const active = isFood ? foodWearers.length > 0 : !!wearer;
  return (
    <ShopFrame testId={`gear-item-${g.item_id}`} color={m.color} alt={m.alt} delay={delay}>
      <div className="relative p-2 flex flex-col h-full">
        <div className="relative aspect-square rounded-xl bg-[#0A051A] mb-2 overflow-hidden border-2" style={{ borderColor: active ? "#39FF14AA" : `${m.color}66` }}>
          <img src={g.image || `/items/${g.item_id}.webp`} alt={g.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
               onError={(e) => { e.currentTarget.style.display = "none"; }} />
          <span className="wr-tile-shine pointer-events-none absolute inset-y-0 -left-1/2 w-1/3 opacity-0 group-hover:opacity-100 group-hover:animate-[wrTileShine_0.8s_ease-out]" />
          <div className="absolute top-1.5 left-1.5 w-6 h-6 rounded-lg flex items-center justify-center bg-black/70 backdrop-blur border" style={{ borderColor: m.color }}>
            <SlotIcon size={12} strokeWidth={2.6} style={{ color: m.color, filter: `drop-shadow(0 0 4px ${m.color})` }} />
          </div>
          <div className="absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded-md bg-black/75 backdrop-blur border text-[10px] lb-num font-bold"
               style={{ borderColor: "#FFE600AA", color: "#FFE600" }} data-testid={`gear-level-${g.item_id}`}>L{g.level}</div>
          {g.drop_only && <div className="absolute bottom-1.5 left-1.5 px-1.5 py-0.5 rounded text-white text-[8px] font-heading uppercase tracking-widest" style={{ background: g.bundle_only ? "#00A8B5" : "#9D00FF" }}>{g.bundle_only ? "Founder" : "Drop"}</div>}
        </div>
        <div className="font-graffiti text-sm leading-tight truncate text-white mb-1.5" style={{ textShadow: `0 0 8px ${m.color}66, 2px 2px 0 #000` }} title={g.name}>{g.name}</div>
        <div className="grid grid-cols-1 gap-[3px] mb-2">
          {Object.entries(g.bonus_pretty || {}).map(([k, v]) => <StatChip key={k} stat={k} value={v} testId={`gear-bonus-${g.item_id}-${k}`} />)}
          <MiniChip icon={Layers} color="#00F0FF" testId={`gear-progress-${g.item_id}`} title="Copies owned / next level">
            {g.copies.toLocaleString()}{maxed ? " · MAX" : ` / ${g.next_at.toLocaleString()}`}
          </MiniChip>
        </div>
        <div className="mt-auto">
          {!isFood && wearer ? (
            <div className="flex flex-col gap-1">
              <div className="px-2 py-1 rounded-md bg-[#39FF14]/10 border border-[#39FF14]/50 font-heading text-[9px] uppercase tracking-widest text-[#39FF14] truncate" data-testid={`gear-wearer-${g.item_id}`}>
                Worn by {wearer.name}
              </div>
              <button data-testid={`gear-unequip-${g.item_id}`} onClick={() => onUnequip(wearer)}
                      className="px-2 py-1 rounded-md font-heading text-[9px] uppercase tracking-widest text-[#FF007F] border border-[#FF007F]/50 hover:bg-[#FF007F]/20 transition-colors">Unequip</button>
            </div>
          ) : (
            <>
              {isFood && foodWearers.length > 0 && (
                <div className="mb-1.5" data-testid={`food-wearers-${g.item_id}`}>
                  <MiniChip icon={UtensilsCrossed} color="#39FF14">Eaten by {foodWearers.length}</MiniChip>
                </div>
              )}
              <button data-testid={`gear-equip-toggle-${g.item_id}`} onClick={() => setOpen(!open)} className="w-full btn-cyan text-[10px] py-1.5">
                {open ? "Cancel" : isFood ? "Feed to…" : "Equip to…"}
              </button>
              {open && <WrestlerPicker g={g} wrestlers={wrestlers} exclude={isFood ? foodWearers.map((f) => f.wrestler_id) : []} onPick={(wid) => { onEquip(wid); setOpen(false); }} />}
            </>
          )}
        </div>
      </div>
    </ShopFrame>
  );
}

const VAULT_BG = "/backgroundgraphics/Wrestlerpage_Background2.webp";

export default function CardsPage() {
  const [cards, setCards] = useState([]);
  const [trophies, setTrophies] = useState([]);
  const [collectibles, setCollectibles] = useState([]);
  const [gearItems, setGearItems] = useState([]);
  const [wrestlers, setWrestlers] = useState([]);
  const [draft, setDraft] = useState({ cap: 0, selected: [], selected_count: 0 });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("cards");
  const [filter, setFilter] = useState("all");
  const [gearSlot, setGearSlot] = useState("all");
  const [popQueue, setPopQueue] = useState([]);
  const clearPops = useCallback(() => setPopQueue([]), []);
  const { user } = useAuth() || {};
  const uid = user?.user_id;

  const load = () =>
    Promise.all([api.get("/cards"), api.get("/trophies"), api.get("/collectibles"), api.get("/inventory"), api.get("/wrestlers"), api.get("/cards/draft-state")])
      .then(([c, t, co, inv, ws, dr]) => {
        setCards(c.data);
        setTrophies(t.data);
        setCollectibles(co.data);
        setGearItems(inv.data);
        setWrestlers(ws.data);
        setDraft(dr.data);
        setLoading(false);
      });

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (loading || !uid) return;
    let rows = [];
    if (tab === "cards") rows = cards.filter(c => c.count > 0).map(c => ({ id: c.id, name: c.name, image: c.image, level: c.tier, levels: true, max: c.max_tier || 4, color: (STAT_META[c.bonus_stat] || STAT_META.all).color }));
    if (tab === "gear") rows = gearItems.map(g => ({ id: g.item_id, name: g.name, image: g.image || `/items/${g.item_id}.webp`, level: g.level, levels: true, max: g.max_level || 4, color: (SLOT_META[g.slot] || SLOT_META.all).color }));
    if (tab === "trophies") rows = trophies.filter(t => t.owned).map(t => ({ id: t.id, name: t.name, image: t.image, level: 1, color: (STAT_META[t.bonus_stat] || STAT_META.all).color }));
    if (tab === "collectibles") rows = collectibles.filter(c => c.qty > 0).map(c => ({ id: c.id, name: c.name, image: c.image, level: 1, color: "#39FF14" }));
    const pops = diffVaultTab(uid, tab, rows);
    if (pops.length) setPopQueue(pops);
  }, [tab, loading, uid, cards, gearItems, trophies, collectibles]);

  const toggleDraft = async (dummyId) => {
    const isSelected = draft.selected.includes(dummyId);
    try {
      const r = await api.post("/cards/draft-select", { dummy_id: dummyId, selected: !isSelected });
      setDraft(r.data);
    } catch (e) {
      alert(e.response?.data?.detail || "Failed");
    }
  };

  const equipGear = async (itemId, wrestlerId) => {
    try {
      await api.post(`/wrestlers/${wrestlerId}/equip`, { item_id: itemId });
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed");
    }
  };
  const unequipGear = async (wearer) => {
    if (!wearer) return;
    try {
      await api.post(`/wrestlers/${wearer.wrestler_id}/unequip`, {
        slot: wearer.slot_kind === "food" ? "food" : wearer.slot,
        slot_index: wearer.slot_kind === "food" ? wearer.slot : 0,
      });
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed");
    }
  };

  if (loading) return <div className="p-8 text-center font-graffiti text-2xl neon-cyan animate-pulse">LOADING VAULT...</div>;

  const maxTier = cards[0]?.max_tier || 4;
  const ownedCards = cards.filter(c => c.count > 0).length;
  const totalCopies = cards.reduce((s, c) => s + c.count, 0);
  const bosses = cards.filter(c => c.is_boss_card);
  const ownedBosses = bosses.filter(c => c.count > 0).length;
  const ownedTrophies = trophies.filter(t => t.owned).length;
  const ownedCol = collectibles.filter(c => c.qty > 0).length;
  const totalColQty = collectibles.reduce((s, c) => s + c.qty, 0);
  const gearCount = (id) => id === "all" ? gearItems.length : gearItems.filter(g => g.slot === id).length;
  const shownGear = gearSlot === "all" ? gearItems : gearItems.filter(g => g.slot === gearSlot);

  let shownCards = cards;
  if (filter === "owned")  shownCards = cards.filter(c => c.count > 0);
  if (filter === "bosses") shownCards = cards.filter(c => c.is_boss_card);

  return (
    <div className="relative max-w-7xl mx-auto px-4 py-8" data-testid="vault-page">
      <StarUpPopup queue={popQueue} onClear={clearPops} />
      <div className="wr-drift pointer-events-none absolute top-10 left-0 w-80 h-80 rounded-full blur-3xl opacity-20 bg-[#FF007F]" />
      <div className="wr-drift pointer-events-none absolute top-[40%] right-0 w-80 h-80 rounded-full blur-3xl opacity-15 bg-[#00F0FF]" style={{ animationDelay: "-6s" }} />
      <div className="relative">
        <div className="flex items-end justify-between mb-5 flex-wrap gap-4">
          <div className="wr-rise">           
            <h1 className="font-graffiti text-4xl sm:text-5xl lg:text-6xl leading-none" data-testid="vault-title">
              <span className="text-[#FF007F]" style={{ textShadow: "0 0 18px #FF007F99, 3px 3px 0 #000" }}>THE</span>{" "}
              <span className="text-[#00F0FF]" style={{ textShadow: "0 0 18px #00F0FF99, 3px 3px 0 #000" }}>VAULT</span>
            </h1>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 w-full sm:w-auto sm:min-w-[520px]">
            <HeroStat icon={Star} label="Cards" color="#00F0FF" testId="vault-stat-cards" value={`${ownedCards}/${cards.length}`} delay={60} />
            <HeroStat icon={Award} label="Trophies" color="#FFE600" testId="vault-stat-trophies" value={`${ownedTrophies}/${trophies.length}`} delay={120} />
            <HeroStat icon={Package} label="Collect." color="#39FF14" testId="vault-stat-collectibles" value={`${ownedCol}/${collectibles.length}`} delay={180} />
            <HeroStat icon={Shirt} label="Gear" color="#FF007F" testId="vault-stat-gear" value={gearItems.length} delay={240} />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-5">
          <VaultTab id="cards" icon={Star} label="Cards" count={`${ownedCards}/${cards.length}`} active={tab === "cards"} onClick={() => setTab("cards")} color="#00F0FF" />
          <VaultTab id="trophies" icon={Award} label="Trophies" count={`${ownedTrophies}/${trophies.length}`} active={tab === "trophies"} onClick={() => setTab("trophies")} color="#FFE600" />
          <VaultTab id="collectibles" icon={Package} label="Collectibles" count={`${ownedCol}/${collectibles.length}`} active={tab === "collectibles"} onClick={() => setTab("collectibles")} color="#39FF14" />
          <VaultTab id="gear" icon={Shirt} label="Gear" count={gearItems.length} active={tab === "gear"} onClick={() => setTab("gear")} color="#FF007F" />
        </div>

        {tab === "cards" && (
          <GradCard delay={80} bg={VAULT_BG} testId="vault-cards-panel">
            <SectionHeading icon={Star} color="#00F0FF">Training Cards</SectionHeading>
            <p className="text-[#C9BDF0] font-mono text-xs tracking-wide mb-4" data-testid="cards-tier-desc">
              Drop from defeated dummies. Tiers scale at <TierScale max={maxTier} /> copies. Each star = <span className="text-[#39FF14]">+1%</span> global stat bonus.
            </p>
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <FilterPill id="all" label="All" count={cards.length} active={filter === "all"} onClick={() => setFilter("all")} />
              <FilterPill id="owned" label="Owned" count={ownedCards} active={filter === "owned"} onClick={() => setFilter("owned")} />
              <FilterPill id="bosses" label="Bosses" count={`${ownedBosses}/${bosses.length}`} active={filter === "bosses"} onClick={() => setFilter("bosses")} color="#9D00FF" />
              <div className="ml-auto flex items-center gap-3 text-[10px] font-mono">
                {draft.cap > 0 && (
                  <span className="text-[#FFE600] px-2.5 py-1 rounded-full border border-[#FFE600]/50 bg-[#0A051A]/80" data-testid="card-drafts-selected-count">
                    <span className="lb-num font-bold">{draft.selected_count}/{draft.cap}</span> Card Draft{draft.cap !== 1 ? "s" : ""} Selected
                  </span>
                )}
                <span className="text-[#A89BCC]">Total copies: <span className="lb-num font-bold text-[#00F0FF]">{totalCopies.toLocaleString()}</span></span>
              </div>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-2">
              {shownCards.map((c, i) => (
                <CardItem key={c.id} card={c} delay={Math.min(i, 20) * 20}
                          draftSelected={draft.selected.includes(c.id)} draftFull={draft.selected_count >= draft.cap} onToggleDraft={toggleDraft} />
              ))}
            </div>
          </GradCard>
        )}

        {tab === "trophies" && (
          <GradCard delay={80} bg={VAULT_BG} testId="vault-trophies-panel">
            <SectionHeading icon={Award} color="#FFE600">Trophies</SectionHeading>
            <p className="text-[#C9BDF0] font-mono text-xs tracking-wide mb-4">
              Unique drops from beating each boss stage. Each trophy gives an <span className="text-[#39FF14]">account-wide additive % bonus</span> to every wrestler.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {trophies.map((t, i) => <TrophyItem key={t.id} t={t} delay={Math.min(i, 12) * 40} />)}
            </div>
          </GradCard>
        )}

        {tab === "collectibles" && (
          <GradCard delay={80} bg={VAULT_BG} testId="vault-collectibles-panel">
            <SectionHeading icon={Package} color="#39FF14">Collectibles</SectionHeading>
            <p className="text-[#C9BDF0] font-mono text-xs tracking-wide mb-4">
              One collectible per opponent. <span className="text-[#39FF14]">Fortune</span> ups the drop chance; <span className="text-[#00F0FF]">Workrate</span> ups the quantity per drop.
              <span className="ml-2 text-[#A89BCC]">Total: <span className="lb-num font-bold text-[#39FF14]">{totalColQty.toLocaleString()}</span></span>
            </p>
            <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-2">
              {collectibles.map((c, i) => <CollectibleItem key={c.id} c={c} delay={Math.min(i, 20) * 20} />)}
            </div>
          </GradCard>
        )}

        {tab === "gear" && (
          <GradCard delay={80} bg={VAULT_BG} testId="vault-gear-panel">
            <SectionHeading icon={Shirt} color="#FF007F">Gear & Food</SectionHeading>
            <p className="text-[#C9BDF0] font-mono text-xs tracking-wide mb-4">
              Click <span className="text-[#00F0FF]">Equip to…</span> to send an item to a wrestler. Gear can only be worn by one wrestler at a time;
              <span className="text-[#39FF14]"> food can be shared</span> by every wrestler (once per wrestler).
              Duplicates level items up: <TierScale max={gearItems[0]?.max_level || maxTier} /> copies, each level multiplies its base bonus (<span className="text-[#39FF14]">Lv2 ×2, Lv3 ×3 …</span>).
            </p>
            <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="gear-slot-filter">
              {["all", "face", "jacket", "boots", "food"].map((id) => (
                <SlotChip key={id} id={id} active={gearSlot === id} count={gearCount(id)} onClick={() => setGearSlot(id)} testPrefix="gear-slot" />
              ))}
            </div>
            {shownGear.length === 0 ? (
              <div className="text-center py-12 text-[#A89BCC] font-mono" data-testid="gear-empty">No gear here yet. Beat the right opponents or hit the Shop.</div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {shownGear.map((g, i) => (
                  <GearItem key={g.item_id} g={g} wrestlers={wrestlers} delay={Math.min(i, 12) * 40}
                            onEquip={(wid) => equipGear(g.item_id, wid)} onUnequip={(w) => unequipGear(w)} />
                ))}
              </div>
            )}
          </GradCard>
        )}
      </div>
    </div>
  );
}
